import { faSquareCaretDown } from "@fortawesome/pro-solid-svg-icons/faSquareCaretDown";
import { faSquareCaretUp } from "@fortawesome/pro-solid-svg-icons/faSquareCaretUp";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Collapse, Grid, IconButton, Stack, Typography } from '@mui/material';
import { MprcButton, MprcContainer, MprcJoinComponents, MprcText } from 'components';
import { MprcButtonProps } from "components/MprcButton";
import { DiscussionListFilter, EnrollStatus } from 'generated/CoreApi_v1';
import { Formaters } from 'lib/Formaters';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEnrollmentViewContext } from "..";
import { CreditsResult } from "../Shared/CreditsResult";
import { DoCloseEnrollment } from "../Shared/DoCloseEnrollment";
import { ReEnrollAction, getEnrollmentCertificate } from "../Shared/EnrollmentActions";
import { EnrollmentCloseButton } from "../Shared/EnrollmentCloseButton";
import { EnrollmentStatus } from "../Shared/EnrollmentStatus";
import { ProgressResult } from "../Shared/ProgressResult";
import { ScoreResult } from "../Shared/ScoreResult";
import { TrainingAvatar } from "../Shared/TrainingAvatar";
import { TrainingDueDate } from "../Shared/TrainingDueDate";
import { TrainingInteractions } from "../Shared/TrainingInteractions";
import { LpEnrollmentViewAbout, LpEnrollmentViewAboutTabs } from "./LpEnrollmentViewAbout";

/////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    initialAboutTab?: LpEnrollmentViewAboutTabs;
    initialDiscussionFilter: DiscussionListFilter;
}

export const LpEnrollmentViewHeader: React.FC<IProps> = ({ initialAboutTab, initialDiscussionFilter }) => {

    const { t } = useTranslation("lms");
    const { enrollment, loading, onReload } = useEnrollmentViewContext();

    const learnPath = enrollment.learnPath!;

    const [dialog, setDialog] = useState<any>();
    const [openAbout, setOpenAbout] = useState<LpEnrollmentViewAboutTabs | null>(initialAboutTab ?? null);
    const [discussionFilter, setDiscussionFilter] = useState<DiscussionListFilter>(initialDiscussionFilter);

    const [closing, setClosing] = useState<boolean>(false);
    const [expanded, setExpanded] = useState<boolean | null>(enrollment.finalizationDate != null);

    /////////////////////////////////////////////////////////////////////////////////////

    ///
    const RenderActions = useMemo(() => {

        var principalActionProps: MprcButtonProps | null =
        {
            text: t("Student.EnrollmentView.LearnPath.OverviewButtonText"),
            onClick: () => setOpenAbout("overview")
        };

        var actions: React.ReactNode[] = [];

        if (enrollment.finalizationDate === null) {

            if (enrollment.canFinish) {
                actions.push(<EnrollmentCloseButton disabled={loading} />);
            }
            else if (enrollment.learnPath!.newDiscussion === true) {
                principalActionProps = {
                    text: t("Student.EnrollmentView.UnseenDiscussionText"),
                    onClick: () => { setOpenAbout("discussion"); setDiscussionFilter(DiscussionListFilter.All); },
                    color: "warning"
                };
            }
            else if (enrollment.learnPath!.newNotices === true) {
                principalActionProps = {
                    text: t("Student.EnrollmentView.UnseenNoticesText"),
                    onClick: () => setOpenAbout("notices"),
                    color: "warning"
                };
            }
        }
        else {
            if (enrollment.hasCertificate && enrollment.certificateId != null) {
                principalActionProps = {
                    text: t("Student.EnrollmentView.CertificateButtonText"),
                    onClick: () => getEnrollmentCertificate(enrollment.certificateId!)
                };
            } else if (enrollment.canReEnroll) {
                principalActionProps = {
                    text: t("Student.EnrollmentView.ReEnrollButtonText"),
                    onClick: () => setDialog(<ReEnrollAction onClose={() => setDialog(null)} />)
                };
            }
        }

        if (principalActionProps != null) {
            actions.push(
                <MprcButton
                    key="principalAction"
                    fullWidth
                    variant="contained"
                    title={principalActionProps.title ?? principalActionProps.text}
                    {...principalActionProps} />
            );
        }

        return (
            <>
                {
                    actions.length > 0 &&
                    <Grid sx={{ width: 240 }}>
                        <Stack direction="row" spacing={2}>
                            {actions}
                        </Stack>
                    </Grid>
                }
            </>
        );
    }, [enrollment, t, initialAboutTab, initialDiscussionFilter]);

    ///
    const RenderMinimezedActions = useMemo(() => {
        var actionProps: MprcButtonProps | null = null;

        if (enrollment.finalizationDate === null) {
            if (enrollment.canFinish && !loading) {
                actionProps = {
                    text: t("Student.EnrollmentView.CompleteButtonText"),
                    onClick: () => setClosing(true),
                    color: "success"
                };
            }
            else if (enrollment.learnPath!.newDiscussion === true) {
                actionProps = {
                    text: t("Student.EnrollmentView.UnseenDiscussionText"),
                    onClick: () => { setOpenAbout("discussion"); setDiscussionFilter(DiscussionListFilter.All); },
                    color: "warning"
                };
            }
            else if (enrollment.learnPath!.newNotices === true) {
                actionProps = {
                    text: t("Student.EnrollmentView.UnseenNoticesText"),
                    onClick: () => setOpenAbout("notices"),
                    color: "warning"
                };
            }
        }
        else {
            if (enrollment.hasCertificate && enrollment.certificateId) {
                actionProps = {
                    text: t("Student.EnrollmentView.CertificateButtonText"),
                    onClick: () => getEnrollmentCertificate(enrollment.certificateId!)
                };
            } else if (enrollment.canReEnroll && (enrollment.status != EnrollStatus.Approved || enrollment.certificateExpired)) {
                actionProps = {
                    text: t("Student.EnrollmentView.ReEnrollButtonText"),
                    onClick: () => setDialog(<ReEnrollAction onClose={() => setDialog(null)} />)
                };
            }
        }
        return actionProps != null && <MprcButton size="small" fullWidth sx={{ p: 0 }} variant="text" title={actionProps.title ?? actionProps.text} {...actionProps} />;
    }, [enrollment, t, loading]);

    ///
    const MinimizedContent = useMemo(() => (
        <Grid container justifyContent="space-between" alignItems="center" direction="row" width="100%">
            <Grid size="grow">
                <Stack direction="row" alignItems="center" gap={8}>
                    {
                        (enrollment.status == EnrollStatus.NotStarted || enrollment.status == EnrollStatus.InProgress)
                            ? (
                                <>
                                    <Box>
                                        {
                                            enrollment.dueDate == null
                                                ? <Typography variant="caption" component="span" color="muted" textTransform="uppercase">{t("Student.EnrollmentView.WithoutDueDateText")}</Typography>
                                                : (
                                                    <>
                                                        <Typography variant="caption" component="span" textTransform="uppercase">
                                                            {t("Student.EnrollmentView.DueDateText")}&nbsp;
                                                        </Typography>
                                                        <Typography variant="caption" fontWeight="bold" component="span">
                                                            <MprcText color={"warning"}>
                                                                <MprcJoinComponents
                                                                    separatorVariant="space"
                                                                    components={[
                                                                        (
                                                                            <MprcText color={"warning"}>
                                                                                {Formaters.performaDate(enrollment.expireDate ?? enrollment.dueDate, { dayOfWeek: false, preposition: "none" })}
                                                                            </MprcText>
                                                                        )
                                                                        , (
                                                                            enrollment.isDelayed &&
                                                                            <MprcText color={"warning"} reverse>
                                                                                {t("Student.EnrollmentView.DueDateOverdueText")}
                                                                            </MprcText>
                                                                        )
                                                                        , (
                                                                            enrollment.isExpired &&
                                                                            <MprcText color={"error"} reverse>
                                                                                {t("Student.EnrollmentView.ExpiredText")}
                                                                            </MprcText>
                                                                        )
                                                                    ]}
                                                                />
                                                            </MprcText>
                                                        </Typography>
                                                    </>
                                                )
                                        }
                                    </Box>
                                    {!enrollment.isPreview &&
                                        (learnPath.progressByCredits ?
                                            <CreditsResult
                                                variant={"inline"}
                                                enableShowDetails
                                                data={{
                                                    progressPercent: enrollment.progressPercent ?? 0,
                                                    credits: learnPath.credits ?? 0,
                                                    enrollmentId: enrollment.enrollmentId,
                                                    isClosed: enrollment.finalizationDate != null,
                                                    maxCredits: learnPath.maxCredits,
                                                    minCredits: learnPath.minCredits,
                                                    status: enrollment.status,
                                                    totalCompletedMandatoryComponents: learnPath.totalCompletedMandatoryComponents,
                                                    totalMandatoryComponents: learnPath.totalMandatoryComponents,
                                                }}
                                            />
                                            :
                                            <ProgressResult
                                                typographyVariant="caption"
                                                variant={"inline"}
                                                data={{
                                                    enrollmentId: enrollment.enrollmentId,
                                                    progress: enrollment.progressPercent,
                                                    minProgress: enrollment.minProgress ?? 0,
                                                    status: enrollment.status,
                                                    isClosed: enrollment.finalizationDate != null,
                                                    totalComponents: enrollment.totalComputeProgressComponents,
                                                    totalCompletedComponents: enrollment.totalCompletedComputeProgressComponents,
                                                    totalCompletedMandatoryComponents: learnPath.totalCompletedMandatoryComponents,
                                                    totalMandatoryComponents: learnPath.totalMandatoryComponents,
                                                }}
                                                enableShowDetails
                                            />
                                        )}
                                    {enrollment.hasScore &&
                                        <Typography variant="caption" component="span" textTransform="uppercase">
                                            {enrollment.totalCompletedScoredComponents == 0 ?
                                                t("Student.EnrollmentView.ScoreText")
                                                : enrollment.finalizationDate != null
                                                    ? t("Student.EnrollmentView.FinalScoreText")
                                                    : t("Student.EnrollmentView.PartialScoreText")
                                            }
                                            : <MprcText color="primary">
                                                <strong>{enrollment.totalCompletedScoredComponents == 0 ? '--' : `${enrollment.scorePercent ?? 0}%`}</strong>
                                            </MprcText>
                                        </Typography>
                                    }
                                </>
                            )
                            : (
                                <Grid>
                                    <EnrollmentStatus variant="inline" data={enrollment} />
                                </Grid>
                            )
                    }
                </Stack>
            </Grid>
            <Grid>
                {RenderMinimezedActions}
            </Grid>
        </Grid>
    ), [enrollment, t, loading]);

    ///
    const RenderTitle = useMemo(() => {
        return (
            <Grid container justifyContent="space-between" alignItems="center" direction="row" wrap="nowrap">
                <Grid size="grow">
                    <Typography variant="h5" color="muted" component="div" noWrap>{enrollment.trainingName}</Typography>
                    {expanded && <Typography variant="caption">{t("Student.EnrollmentView.LearnPath.ModuleText", { count: enrollment.totalComponents })}</Typography>}
                </Grid>
                <Grid>
                    <IconButton onClick={() => setExpanded(!expanded)} sx={{ p: 0 }}>
                        <FontAwesomeIcon icon={expanded ? faSquareCaretUp : faSquareCaretDown} color="muted.main" />
                    </IconButton>
                </Grid>
            </Grid>
        );
    }, [enrollment, expanded, t]);

    ///
    const RenderContent = useMemo(() => {

        return (
            <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                    <Grid container spacing={2} wrap="nowrap">
                        <Grid>
                            <TrainingAvatar />
                        </Grid>
                        <Grid size="grow">
                            {enrollment.finalizationDate != null &&
                                <MprcContainer><EnrollmentStatus variant="block" data={enrollment} />
                                </MprcContainer>
                            }
                            {!enrollment.isPreview &&
                                <MprcContainer>
                                    <Grid container spacing={8}>
                                        <Grid>
                                            {learnPath.progressByCredits ?
                                                <CreditsResult
                                                    variant={"block"}
                                                    data={{
                                                        progressPercent: enrollment.progressPercent ?? 0,
                                                        credits: learnPath.credits ?? 0,
                                                        enrollmentId: enrollment.enrollmentId,
                                                        isClosed: enrollment.finalizationDate != null,
                                                        maxCredits: learnPath.maxCredits,
                                                        minCredits: learnPath.minCredits,
                                                        status: enrollment.status,
                                                        totalCompletedMandatoryComponents: learnPath.totalCompletedMandatoryComponents,
                                                        totalMandatoryComponents: learnPath.totalMandatoryComponents,
                                                    }}
                                                    enableShowDetails
                                                />
                                                :
                                                <ProgressResult
                                                    typographyVariant="caption"
                                                    variant={"block"}
                                                    data={{
                                                        enrollmentId: enrollment.enrollmentId,
                                                        progress: enrollment.progressPercent,
                                                        minProgress: enrollment.minProgress ?? 0,
                                                        status: enrollment.status,
                                                        isClosed: enrollment.finalizationDate != null,
                                                        totalComponents: enrollment.totalComputeProgressComponents,
                                                        totalCompletedComponents: enrollment.totalCompletedComputeProgressComponents,
                                                        totalCompletedMandatoryComponents: learnPath.totalCompletedMandatoryComponents,
                                                        totalMandatoryComponents: learnPath.totalMandatoryComponents,
                                                    }}
                                                    enableShowDetails
                                                />
                                            }
                                        </Grid>
                                        {
                                            enrollment.hasScore &&
                                            <Grid>
                                                <ScoreResult
                                                    variant={"multiple"}
                                                    enrollmentId={enrollment.enrollmentId}
                                                    score={enrollment.scorePercent}
                                                    minScore={enrollment.minFinalScore ?? 0}
                                                    isClosed={enrollment.finalizationDate != null}
                                                    totalItems={enrollment.totalScoredComponents}
                                                    totalCompletedItems={enrollment.totalCompletedScoredComponents}
                                                    status={enrollment.status}
                                                    enableShowDetails
                                                />
                                            </Grid>
                                        }
                                    </Grid>
                                </MprcContainer>
                            }
                            {(enrollment.status == EnrollStatus.NotStarted || enrollment.status == EnrollStatus.InProgress) &&
                                <MprcContainer>
                                    <TrainingDueDate enrollment={enrollment} />
                                </MprcContainer>
                            }
                        </Grid>
                    </Grid>
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <Grid container spacing={2}>
                        {RenderActions}
                        <Grid size="grow">
                            <TrainingInteractions enrollment={enrollment} readOnly={enrollment.isPreview} />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        );
    }, [enrollment, t]);


    //////////////////////////////////////////////////////
    /// RENDER
    //////////////////////////////////////////////////////

    return (
        <MprcContainer>
            <Box sx={theme => ({
                backgroundColor: theme.palette.common.white,
                marginLeft: theme.spacing(-2),
                marginRight: theme.spacing(-2),
                borderBottom: "solid 1px",
                borderBottomColor: theme.extended.workArea.navigationBar.separatorColor,
            })}>
                {dialog}
                {openAbout != null &&
                    <LpEnrollmentViewAbout
                        aboutTab={openAbout}
                        initialDiscussionFilter={discussionFilter}
                        onClose={() => setOpenAbout(null)}
                    />
                }
                <Stack direction="row" gap={1} alignItems="center" p={1}>
                    {!expanded && <TrainingAvatar size="extra-small" />}
                    <Grid container direction="row" width="100%">
                        <Grid size={{ xs: 12 }}>
                            {RenderTitle}
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Collapse in={expanded ?? true} unmountOnExit>
                                {RenderContent}
                            </Collapse>
                            {!expanded && MinimizedContent}
                        </Grid>
                    </Grid>
                </Stack>
                <DoCloseEnrollment
                    open={closing}
                    onAction={
                        (status) => {
                            setClosing(false);
                            if (status == "closed") onReload();
                        }
                    }
                />
            </Box>
        </MprcContainer>
    );
};
